import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Lock, FileSpreadsheet, FileText } from "lucide-react";
import { api, apiError, fmtDate, fmtTime } from "../lib/api";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

async function downloadExport(url, fallbackName) {
    try {
        const token = localStorage.getItem("token");
        const res = await fetch(BACKEND + url, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        const cd = res.headers.get("content-disposition") || "";
        const m = cd.match(/filename="?([^";]+)"?/);
        const filename = m ? m[1] : fallbackName;
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = filename; link.click();
        URL.revokeObjectURL(link.href);
    } catch (e) { toast.error(String(e.message || e)); }
}

function ymOfDate(d) {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function MonthlyTrackerPage() {
    const { siteId } = useParams();
    const [site, setSite] = useState(null);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [month, setMonth] = useState(ymOfDate(new Date()));

    useEffect(() => {
        (async () => {
            try {
                const s = await api.get(`/sites/${siteId}`);
                setSite(s.data);
            } catch (e) {
                toast.error(apiError(e));
            }
        })();
    }, [siteId]);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get(`/sites/${siteId}/runs?month=${month}`);
                setRows(data);
            } catch (e) {
                toast.error(apiError(e));
            } finally {
                setLoading(false);
            }
        })();
    }, [siteId, month]);

    const totals = useMemo(() => {
        return {
            days: rows.length,
            signedOff: rows.filter((r) => r.sign_off_time).length,
            escalations: rows.reduce((s, r) => s + (r.escalations || 0), 0),
        };
    }, [rows]);

    function shiftMonth(delta) {
        const [y, m] = month.split("-").map(Number);
        const d = new Date(y, m - 1 + delta, 1);
        setMonth(ymOfDate(d));
    }

    const monthLabel = useMemo(() => {
        const [y, m] = month.split("-");
        return new Date(Number(y), Number(m) - 1, 1).toLocaleString("en-IN", { month: "long", year: "numeric" });
    }, [month]);

    return (
        <div className="mx-auto max-w-6xl px-4 sm:px-6 py-6 sm:py-10">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <div className="text-xs font-medium uppercase tracking-wider text-gray-500">Monthly Tracker</div>
                    <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1E3355] mt-1">{site?.name || "…"}</h1>
                    {site?.address && <div className="text-sm text-gray-500 mt-0.5">{site.address}</div>}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <button onClick={() => downloadExport(`/api/sites/${siteId}/tracker/export.csv?month=${month}`, `checklist-tracker-${month}.csv`)} className="inline-flex items-center gap-1 rounded-md border border-[#1E3355] text-[#1E3355] hover:bg-[#F4F6F9] px-3 py-2 text-xs font-semibold" data-testid="tracker-export-csv"><FileSpreadsheet className="w-3.5 h-3.5" /> CSV</button>
                    <button onClick={() => downloadExport(`/api/sites/${siteId}/tracker/export.pdf?month=${month}`, `checklist-tracker-${month}.pdf`)} className="inline-flex items-center gap-1 rounded-md border border-[#1E3355] text-[#1E3355] hover:bg-[#F4F6F9] px-3 py-2 text-xs font-semibold" data-testid="tracker-export-pdf"><FileText className="w-3.5 h-3.5" /> PDF</button>
                    <div className="flex items-center gap-2 bg-white border border-gray-200 rounded-md p-1" data-testid="month-picker">
                    <button onClick={() => shiftMonth(-1)} className="p-1.5 rounded hover:bg-gray-100" data-testid="month-prev">
                        <ChevronLeft className="w-4 h-4 text-gray-600" />
                    </button>
                    <div className="px-3 text-sm font-medium text-[#1E3355] min-w-[140px] text-center">{monthLabel}</div>
                    <button onClick={() => shiftMonth(1)} className="p-1.5 rounded hover:bg-gray-100" data-testid="month-next">
                        <ChevronRight className="w-4 h-4 text-gray-600" />
                    </button>
                    </div>
                </div>
            </div>

            <div className="mt-6 grid grid-cols-3 gap-3 sm:gap-4">
                <StatCard label="Runs" value={totals.days} />
                <StatCard label="Signed off" value={totals.signedOff} />
                <StatCard label="Escalations" value={totals.escalations} accent />
            </div>

            <div className="mt-6 rounded-xl bg-white border border-gray-100 fx-shadow-card overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="tracker-table">
                        <thead className="bg-[#F4F6F9]">
                            <tr className="text-left text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                                <th className="px-4 py-3">Date</th>
                                <th className="px-4 py-3">Cycle · Theme</th>
                                <th className="px-4 py-3 text-center">Done</th>
                                <th className="px-4 py-3 text-center">Critical</th>
                                <th className="px-4 py-3 text-center">Escalations</th>
                                <th className="px-4 py-3">Sign-off</th>
                                <th className="px-4 py-3">Manager</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {loading ? (
                                <tr><td colSpan="7" className="p-6 text-center text-sm text-gray-500">Loading…</td></tr>
                            ) : rows.length === 0 ? (
                                <tr><td colSpan="7" className="p-8 text-center text-sm text-gray-500" data-testid="tracker-empty">No runs for {monthLabel}.</td></tr>
                            ) : rows.map((r) => (
                                <tr key={r.id} className="hover:bg-[#FAFBFC] cursor-pointer" data-testid={`tracker-row-${r.run_date}`} onClick={() => (window.location.href = `/sites/${siteId}/runs/${r.id}`)}>
                                    <td className="px-4 py-3 font-medium text-[#1E3355]">{fmtDate(r.run_date)}</td>
                                    <td className="px-4 py-3">
                                        <div className="text-xs text-gray-500">Day {r.cycle_day}</div>
                                        <div className="text-sm">{r.theme}</div>
                                    </td>
                                    <td className="px-4 py-3 text-center tabular-nums">{r.done}/{r.total}</td>
                                    <td className="px-4 py-3 text-center tabular-nums">{r.critical_done}/{r.critical_total}</td>
                                    <td className="px-4 py-3 text-center">
                                        {r.escalations > 0 ? (
                                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-[#E02B2B]">{r.escalations}</span>
                                        ) : <span className="text-gray-400">—</span>}
                                    </td>
                                    <td className="px-4 py-3 text-xs">
                                        {r.sign_off_time ? (
                                            <span className="inline-flex items-center gap-1 text-emerald-700 font-medium">
                                                <Lock className="w-3 h-3" /> {fmtTime(r.sign_off_time)}
                                            </span>
                                        ) : <span className="text-gray-400">Pending</span>}
                                    </td>
                                    <td className="px-4 py-3 text-xs text-gray-600">{r.manager_name}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

function StatCard({ label, value, accent }) {
    return (
        <div className="rounded-xl bg-white border border-gray-100 fx-shadow-card p-4">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-500">{label}</div>
            <div className={`mt-1 font-display text-2xl font-bold ${accent ? "text-[#E02B2B]" : "text-[#1E3355]"}`}>{value}</div>
        </div>
    );
}
